/*
 * VILLAIN - Add To Cart property tracking for GA4 / GTM
 *
 * Purpose:
 * - Detect every successful Shopify /cart/add request used by the theme.
 * - Capture Shopify line-item properties (properties[...]) when present.
 * - Push a GTM-friendly dataLayer event.
 * - Send a separate custom GA4 event so existing Shopify/GA4 add_to_cart
 *   tracking is not duplicated.
 *
 * GA4 event: villain_add_to_cart
 */
(function () {
  'use strict';

  if (window.__villainAtcGa4TrackingLoaded) return;
  window.__villainAtcGa4TrackingLoaded = true;

  window.dataLayer = window.dataLayer || [];

  var originalFetch = window.fetch;

  function getAddToCartUrl(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && input.url) return input.url;
    } catch (e) {}
    return '';
  }

  function isAddToCartRequest(input, init) {
    var url = getAddToCartUrl(input);
    if (!url) return false;

    try {
      var parsed = new URL(url, window.location.origin);
      var path = parsed.pathname.replace(/\/$/, '');
      if (path !== '/cart/add' && path !== '/cart/add.js') return false;

      var method = (init && init.method) || (input && input.method) || 'GET';
      return String(method).toUpperCase() === 'POST';
    } catch (e) {
      return false;
    }
  }

  function normalizeProperties(properties) {
    var result = {};
    if (!properties || typeof properties !== 'object') return result;

    Object.keys(properties).forEach(function (key) {
      if (properties[key] === null || properties[key] === undefined || properties[key] === '') return;
      result[String(key)] = String(properties[key]);
    });

    return result;
  }

  function parseRequestBody(input, init) {
    var body = init && init.body !== undefined ? init.body : input && input.body;
    var result = { properties: {}, quantity: null, id: null };

    if (!body) return result;

    try {
      if (typeof body === 'string') {
        var parsed = JSON.parse(body);
        if (parsed && typeof parsed === 'object') {
          if (parsed.items && Array.isArray(parsed.items)) return { items: parsed.items };
          result.properties = normalizeProperties(parsed.properties);
          result.quantity = parsed.quantity != null ? Number(parsed.quantity) : null;
          result.id = parsed.id != null ? String(parsed.id) : null;
          return result;
        }
      }

      if (body instanceof FormData) {
        body.forEach(function (value, key) {
          if (/^properties\[.+\]$/.test(key)) {
            var propertyName = key.replace(/^properties\[/, '').replace(/\]$/, '');
            result.properties[propertyName] = String(value);
          } else if (key === 'quantity') {
            result.quantity = Number(value);
          } else if (key === 'id') {
            result.id = String(value);
          }
        });
        return result;
      }

      if (body instanceof URLSearchParams) {
        body.forEach(function (value, key) {
          if (/^properties\[.+\]$/.test(key)) {
            var propertyName = key.replace(/^properties\[/, '').replace(/\]$/, '');
            result.properties[propertyName] = String(value);
          } else if (key === 'quantity') {
            result.quantity = Number(value);
          } else if (key === 'id') {
            result.id = String(value);
          }
        });
      }
    } catch (e) {
      // Tracking must never interrupt add-to-cart functionality.
    }

    return result;
  }

  function normalizeItems(responseData, requestData) {
    var sourceItems = [];

    if (responseData && Array.isArray(responseData.items)) {
      sourceItems = responseData.items;
    } else if (responseData && (responseData.id || responseData.variant_id)) {
      sourceItems = [responseData];
    } else if (requestData && Array.isArray(requestData.items)) {
      sourceItems = requestData.items;
    } else if (requestData && (requestData.id || requestData.variant_id)) {
      sourceItems = [requestData];
    }

    return sourceItems.map(function (item) {
      var properties = normalizeProperties(item.properties);
      if (!Object.keys(properties).length && requestData && requestData.properties) {
        properties = normalizeProperties(requestData.properties);
      }

      var quantity = Number(item.quantity || (requestData && requestData.quantity) || 1);
      var price = Number(item.price || 0);
      var value = price && quantity ? (price * quantity) / 100 : 0;

      return {
        item_id: String(item.variant_id || item.id || ''),
        item_name: String(item.product_title || item.product_name || item.title || ''),
        item_variant: String(item.variant_title || ''),
        price: price ? price / 100 : 0,
        quantity: quantity,
        value: value,
        properties: properties
      };
    });
  }

  function buildPropertySummary(items) {
    var merged = {};

    items.forEach(function (item) {
      Object.keys(item.properties || {}).forEach(function (key) {
        if (merged[key] === undefined) {
          merged[key] = item.properties[key];
        } else if (merged[key] !== item.properties[key]) {
          merged[key] = merged[key] + ' | ' + item.properties[key];
        }
      });
    });

    var names = Object.keys(merged);
    return {
      properties: merged,
      names: names,
      count: names.length,
      used: names.length > 0
    };
  }

  function trackAddToCart(responseData, requestData) {
    try {
      if (responseData && responseData.status && Number(responseData.status) >= 400) return;

      var items = normalizeItems(responseData, requestData);
      if (!items.length) return;

      var propertySummary = buildPropertySummary(items);
      var totalValue = items.reduce(function (sum, item) {
        return sum + Number(item.value || 0);
      }, 0);
      var totalQuantity = items.reduce(function (sum, item) {
        return sum + Number(item.quantity || 0);
      }, 0);

      var ecommerceItems = items.map(function (item) {
        return {
          item_id: item.item_id,
          item_name: item.item_name,
          item_variant: item.item_variant,
          price: item.price,
          quantity: item.quantity,
          item_property_names: Object.keys(item.properties || {}).join(', '),
          item_properties: JSON.stringify(item.properties || {})
        };
      });

      var eventData = {
        event: 'villain_add_to_cart',
        villain_atc_property_used: propertySummary.used,
        villain_atc_property_count: propertySummary.count,
        villain_atc_property_names: propertySummary.names.join(', '),
        villain_atc_properties_json: JSON.stringify(propertySummary.properties),
        villain_atc_quantity: totalQuantity,
        villain_atc_value: totalValue,
        villain_atc_currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR',
        ecommerce: {
          currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR',
          value: totalValue,
          items: ecommerceItems
        }
      };

      window.dataLayer.push(eventData);

      // Separate event name prevents duplicate standard add_to_cart events.
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'villain_add_to_cart', {
          villain_atc_property_used: propertySummary.used,
          villain_atc_property_count: propertySummary.count,
          villain_atc_property_names: propertySummary.names.join(', '),
          villain_atc_properties_json: JSON.stringify(propertySummary.properties),
          villain_atc_quantity: totalQuantity,
          value: totalValue,
          currency: eventData.villain_atc_currency,
          items: ecommerceItems
        });
      }
    } catch (e) {
      // Never block the customer's cart action because of analytics.
      console.warn('[VILLAIN GA4] Add-to-cart tracking error:', e);
    }
  }

  window.fetch = function () {
    var args = Array.prototype.slice.call(arguments);
    var input = args[0];
    var init = args[1];
    var shouldTrack = isAddToCartRequest(input, init);
    var requestData = shouldTrack ? parseRequestBody(input, init) : null;

    var fetchResult = originalFetch.apply(this, args);

    if (!shouldTrack || !fetchResult || typeof fetchResult.then !== 'function') {
      return fetchResult;
    }

    return fetchResult.then(function (response) {
      try {
        var responseClone = response.clone();
        responseClone.json().then(function (responseData) {
          trackAddToCart(responseData, requestData);
        }).catch(function () {
          // Ignore non-JSON responses.
        });
      } catch (e) {
        // Ignore tracking errors.
      }
      return response;
    });
  };
})();
